<template>
  <div>
    <b-modal
        v-model="showModalInternal"
        id="visual-report-chart-form-modal"
        scrollable
        :title="isEditMode ? 'Edytuj wykres' : 'Utwórz wykres'"
        title-class="font-18"
        size="lg"
        hide-footer
    >

      <div v-if="saving" class="text-center py-5">
        <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
        <p class="mt-2 text-muted">Zapisywanie wykresu...</p>
      </div>

      <div v-else-if="loadingMetadata" class="text-center py-5">
        <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
        <p class="mt-2 text-muted">Ładowanie metadanych wykresu...</p>
      </div>

      <div v-else>

        <!-- Basic settings -->
        <div class="card shadow-sm mb-4">
          <div class="card-header bg-light">
            <h6 class="mb-0 text-primary fw-bold">
              <i class="fas fa-info-circle me-2"></i>Podstawowe ustawienia
            </h6>
          </div>
          <div class="card-body">
            <div class="row g-3">

              <div class="col-md-8">
                <label class="form-label required">Tytuł wykresu</label>
                <input
                    type="text"
                    class="form-control"
                    v-model="formData.title"
                    placeholder="Wprowadź tytuł wykresu"
                />
              </div>

              <div class="col-md-4">
                <label class="form-label required">Typ wykresu</label>
                <select class="form-select" v-model="formData.chartType">
                  <option value="bar">Słupkowy (bar)</option>
                  <option value="line">Liniowy (line)</option>
                  <option value="pie">Kołowy (pie)</option>
                </select>
              </div>

              <div class="col-md-6">
                <label class="form-label required">Oś X / Kategoria</label>
                <select class="form-select" v-model="formData.xAxisKey">
                  <option value="">-- Wybierz kolumnę --</option>
                  <option
                      v-for="col in categoryColumns"
                      :key="col.key"
                      :value="col.key"
                  >
                    {{ col.text || col.key }}
                  </option>
                </select>
                <div class="form-text text-muted small">Kolumna kategorii (oś pozioma lub etykiety).</div>
              </div>

              <!-- Y axis (measure) — shown when aggregation metadata available -->
              <div v-if="yAxisColumns.length > 0" class="col-md-6">
                <label class="form-label required">Oś Y / Miara</label>
                <select class="form-select" v-model="formData.yAxisColumn">
                  <option value="">-- Wybierz miarę --</option>
                  <option
                      v-for="col in yAxisColumns"
                      :key="col.key"
                      :value="col.key"
                  >{{ col.text || col.key }}</option>
                </select>
                <div class="form-text text-muted small">Kolumna numeryczna / miara (oś pionowa).</div>
              </div>

              <div class="col-md-6">
                <label class="form-label required">Serie danych (grupowanie)</label>
                <div class="border rounded p-2" style="max-height: 150px; overflow-y: auto;">
                  <div
                      v-for="col in categoryColumns"
                      :key="col.key"
                      class="form-check"
                  >
                    <input
                        class="form-check-input"
                        type="checkbox"
                        :id="'series-' + col.key"
                        :value="col.key"
                        v-model="formData.seriesKeys"
                    />
                    <label class="form-check-label small" :for="'series-' + col.key">
                      {{ col.text || col.key }}
                    </label>
                  </div>
                  <div v-if="categoryColumns.length === 0" class="text-muted small py-1">
                    Brak dostępnych kolumn grupowania
                  </div>
                </div>
                <div class="form-text text-muted small">
                  Kolumny grupowania serii (opcjonalne).
                </div>
              </div>

              <div class="col-md-6">
                <label class="form-label">Typ agregacji</label>
                <select class="form-select" v-model="formData.aggregationType">
                  <option v-for="agg in availableAggregations" :key="agg" :value="agg">{{ agg }}</option>
                </select>
                <div v-if="selectedYAxisSpec" class="form-text text-muted small">
                  Dozwolone funkcje dla wybranej miary.
                </div>
              </div>

              <div class="col-md-6 d-flex align-items-end">
                <div class="form-check form-switch mt-2">
                  <input
                      class="form-check-input"
                      type="checkbox"
                      id="chart-visible-switch"
                      v-model="formData.visible"
                  />
                  <label class="form-check-label" for="chart-visible-switch">
                    Wykres widoczny
                  </label>
                </div>
              </div>

            </div>
          </div>
        </div>

        <!-- Chart display settings -->
        <div class="card shadow-sm mb-4">
          <div class="card-header bg-light">
            <h6 class="mb-0 text-primary fw-bold">
              <i class="fas fa-sliders-h me-2"></i>Ustawienia wyświetlania
            </h6>
          </div>
          <div class="card-body">
            <div class="row g-3">

              <div class="col-md-6">
                <label class="form-label">Wysokość (px)</label>
                <input
                    type="number"
                    class="form-control form-control-sm"
                    v-model.number="formData.chartSettings.height"
                    min="150"
                    max="800"
                    step="50"
                />
              </div>

              <div class="col-md-6 d-flex align-items-end gap-3 pb-1">
                <div class="form-check form-switch">
                  <input
                      class="form-check-input"
                      type="checkbox"
                      id="chart-show-legend"
                      v-model="formData.chartSettings.showLegend"
                  />
                  <label class="form-check-label small" for="chart-show-legend">Legenda</label>
                </div>
                <div class="form-check form-switch">
                  <input
                      class="form-check-input"
                      type="checkbox"
                      id="chart-show-data-labels"
                      v-model="formData.chartSettings.showDataLabels"
                  />
                  <label class="form-check-label small" for="chart-show-data-labels">Etykiety danych</label>
                </div>
              </div>

            </div>
          </div>
        </div>

        <!-- Validation errors -->
        <div v-if="validationErrors.length > 0" class="alert alert-danger py-2">
          <ul class="mb-0 ps-3">
            <li v-for="(err, idx) in validationErrors" :key="idx">{{ err }}</li>
          </ul>
        </div>

        <!-- Footer actions -->
        <div class="modal-footer-container">
          <div class="modal-footer-bottom">
            <button type="button" class="btn btn-secondary" @click="cancel" :disabled="saving">
              <i class="fas fa-times me-1"></i>Anuluj
            </button>
            <button type="button" class="btn btn-primary" @click="save" :disabled="saving">
              <i class="fas fa-save me-1"></i>Zapisz
            </button>
          </div>
        </div>

      </div>

    </b-modal>
  </div>
</template>

<script>
import {BModal} from "bootstrap-vue-next";
import {log} from "@/state/pinia/log";
import {useVisualReportsModule} from "@/state/pinia/visualReportsModule";

export default {
  name: "VisualReportChartFormModal",
  components: {
    BModal,
  },
  props: {
    showModal: {
      type: Boolean,
      required: true,
    },
    reportGuid: {
      type: String,
      required: true,
    },
    reportType: {
      type: String,
      default: null,
    },
    editChart: {
      type: Object,
      default: null,
    },
    availableColumns: {
      type: Array,
      default: () => [],
    },
    aggregationMetadata: {
      type: Object,
      default: null,
    },
  },
  emits: ["update:showModal", "onSave", "onClose"],
  data() {
    return {
      showModalInternal: this.showModal,
      saving: false,
      loadingMetadata: false,
      chartMetadata: null,
      validationErrors: [],
      formData: {
        title: "",
        chartType: "bar",
        xAxisKey: "",
        yAxisColumn: "",
        seriesKeys: [],
        aggregationType: "SUM",
        stacked: false,
        visible: true,
        chartSettings: {
          height: 350,
          showLegend: true,
          showDataLabels: false,
        },
      },
    };
  },
  computed: {
    visualReportsModule() {
      return useVisualReportsModule();
    },
    isEditMode() {
      return !!this.editChart;
    },
    categoryColumns() {
      // Prefer aggregation metadata groupable columns (unified source of truth)
      if (this.aggregationMetadata && this.aggregationMetadata.groupableColumns && this.aggregationMetadata.groupableColumns.length > 0) {
        return this.aggregationMetadata.groupableColumns.map((col) => ({
          key: col.key,
          text: col.label || col.key,
        }));
      }
      // Fallback: chart metadata categorical columns
      if (this.chartMetadata && this.chartMetadata.categoricalColumns && this.chartMetadata.categoricalColumns.length > 0) {
        return this.chartMetadata.categoricalColumns.map((key) => ({
          key,
          text: this.getColumnLabel(key),
        }));
      }
      // Fallback: derive from availableColumns
      if (!this.availableColumns || this.availableColumns.length === 0) return [];
      const chartable = this.availableColumns.filter((c) => c.chartable === true);
      if (chartable.length > 0) return chartable;
      return this.availableColumns;
    },
    yAxisColumns() {
      // Prefer aggregation metadata measures as Y axis options
      if (this.aggregationMetadata && this.aggregationMetadata.measures && this.aggregationMetadata.measures.length > 0) {
        return this.aggregationMetadata.measures.map((spec) => ({
          key: spec.key,
          text: spec.label || spec.key,
          allowedFunctions: spec.allowedFunctions || [],
          defaultFunction: spec.defaultFunction || null,
        }));
      }
      return [];
    },
    numericColumns() {
      // Use yAxisColumns when aggregation metadata is available
      if (this.yAxisColumns.length > 0) return this.yAxisColumns;
      // Fallback: chart metadata numeric columns
      if (this.chartMetadata && this.chartMetadata.numericColumns && this.chartMetadata.numericColumns.length > 0) {
        return this.chartMetadata.numericColumns.map((key) => ({
          key,
          text: this.getColumnLabel(key),
        }));
      }
      // Fallback: derive from availableColumns
      if (!this.availableColumns || this.availableColumns.length === 0) return [];
      const chartable = this.availableColumns.filter((c) => c.chartable === true);
      if (chartable.length > 0) return chartable;
      const numericTypes = ["number", "currency", "float", "decimal", "integer", "int"];
      return this.availableColumns.filter((c) => {
        const t = (c.type || c.columnType || c.dataType || "").toLowerCase();
        return numericTypes.some((nt) => t.includes(nt));
      });
    },
    selectedYAxisSpec() {
      if (!this.formData.yAxisColumn) return null;
      return this.yAxisColumns.find((c) => c.key === this.formData.yAxisColumn) || null;
    },
    availableAggregations() {
      // Use allowed functions from the selected Y axis measure
      if (this.selectedYAxisSpec && this.selectedYAxisSpec.allowedFunctions && this.selectedYAxisSpec.allowedFunctions.length > 0) {
        return this.selectedYAxisSpec.allowedFunctions;
      }
      if (this.chartMetadata && this.chartMetadata.aggregations && this.chartMetadata.aggregations.length > 0) {
        return this.chartMetadata.aggregations;
      }
      return ["NONE", "SUM", "COUNT", "AVG"];
    },
  },
  methods: {
    getColumnLabel(key) {
      // Try to find a display label from availableColumns (richer objects from viewConfig)
      if (this.availableColumns && this.availableColumns.length > 0) {
        const found = this.availableColumns.find((c) => (c.key || c) === key);
        if (found && typeof found === "object") {
          return found.displayName || found.text || found.name || key;
        }
      }
      // Try metadata availableColumns
      if (this.chartMetadata && this.chartMetadata.availableColumns) {
        const found = this.chartMetadata.availableColumns.find((c) => {
          return typeof c === "string" ? c === key : (c.key || c) === key;
        });
        if (found && typeof found === "object") {
          return found.displayName || found.text || found.name || key;
        }
      }
      return key;
    },

    loadMetadata() {
      if (!this.reportType) return;
      this.loadingMetadata = true;
      this.visualReportsModule.httpGetVisualReportChartMetadata(this.reportType).then((response) => {
        if (response && response.status === 200) {
          this.chartMetadata = response.data.payload || null;
          console.log("VisualReportChartFormModal: chart metadata loaded:", this.chartMetadata);
        }
      }).finally(() => {
        this.loadingMetadata = false;
      });
    },

    init() {
      if (this.isEditMode && this.editChart) {
        this.formData = {
          title: this.editChart.title || "",
          chartType: this.editChart.chartType || "bar",
          xAxisKey: this.editChart.xAxisKey || this.editChart.xAxisColumn || "",
          yAxisColumn: this.editChart.yAxisColumn || "",
          seriesKeys: this.editChart.seriesKeys
              ? JSON.parse(JSON.stringify(this.editChart.seriesKeys))
              : (this.editChart.seriesColumns ? JSON.parse(JSON.stringify(this.editChart.seriesColumns)) : []),
          aggregationType: this.editChart.aggregationType || "SUM",
          stacked: this.editChart.stacked === true,
          visible: this.editChart.visible !== undefined ? this.editChart.visible : (this.editChart.enabled !== false),
          chartSettings: {
            height: (this.editChart.chartSettings && this.editChart.chartSettings.height) || 350,
            showLegend: (this.editChart.chartSettings && this.editChart.chartSettings.showLegend !== undefined) ? this.editChart.chartSettings.showLegend : true,
            showDataLabels: (this.editChart.chartSettings && this.editChart.chartSettings.showDataLabels !== undefined) ? this.editChart.chartSettings.showDataLabels : false,
          },
        };
      } else {
        this.formData = {
          title: "",
          chartType: "bar",
          xAxisKey: "",
          yAxisColumn: "",
          seriesKeys: [],
          aggregationType: "SUM",
          stacked: false,
          visible: true,
          chartSettings: {
            height: 350,
            showLegend: true,
            showDataLabels: false,
          },
        };
      }
      this.validationErrors = [];
    },

    validateLocalForm() {
      this.validationErrors = [];
      if (!this.formData.title || this.formData.title.trim() === "") {
        this.validationErrors.push("Tytuł wykresu jest wymagany");
      }
      if (!this.formData.xAxisKey) {
        this.validationErrors.push("Wybór kolumny osi X jest wymagany");
      }
      if (this.yAxisColumns.length > 0 && !this.formData.yAxisColumn) {
        this.validationErrors.push("Wybór miary (oś Y) jest wymagany");
      }
      if(this.validationErrors.length > 0) {
        console.log("VisualReportChartFormModal: validation errors:", this.validationErrors);
      }
      return this.validationErrors.length === 0;
    },

    save() {
      if (!this.validateLocalForm()) return;

      this.saving = true;

      const backendPayload = {
        guid: this.editChart ? (this.editChart.guid || null) : null,
        configGuid: this.editChart ? (this.editChart.configGuid || null) : null,
        chartType: (this.formData.chartType || "bar").toUpperCase(),
        xAxisColumn: this.formData.xAxisKey || "",
        yAxisColumn: this.formData.yAxisColumn || "",
        seriesColumns: this.formData.seriesKeys || [],
        aggregationType: this.formData.aggregationType || "COUNT",
        stacked: this.formData.stacked === true,
        enabled: this.formData.visible !== false,
      };
      console.log("VisualReportChartFormModal: saving chart config:", backendPayload);
      this.visualReportsModule.httpSaveVisualReportChartConfig(this.reportGuid, backendPayload).then((response) => {
        if (response && response.status === 200) {
          log.log("VisualReportChartFormModal: chart config saved:", response.data.payload);
          this.$emit("onSave", response.data.payload);
          this.closeModal();
        }
      }).finally(() => {
        this.saving = false;
      });
    },

    cancel() {
      this.closeModal();
    },
    closeModal() {
      this.showModalInternal = false;
      this.$emit("onClose");
    },
  },
  watch: {
    showModal(newValue) {
      this.showModalInternal = newValue;
      if (newValue) {
        this.init();
        this.loadMetadata();
      }
    },
    showModalInternal(newValue) {
      this.$emit("update:showModal", newValue);
    },
  },
  created() {
    log.log("VisualReportChartFormModal created, reportGuid:", this.reportGuid, "reportType:", this.reportType);
    this.init();
  },
};
</script>

<style scoped>
.modal-footer-container {
  position: sticky;
  bottom: -19px;
  left: 0;
  width: 100%;
  background: #fff;
  z-index: 60;
  padding: 0;
  border-top: 1px solid #dee2e6;
  margin-top: 16px;
}

.modal-footer-bottom {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 14px 0 4px 0;
}

.required::after {
  content: " *";
  color: #f46a6a;
}
</style>
